import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { ButtonLink } from '@/components/generals/button'
import { TransitionLink } from '@/components/generals/transition-link'
import { Reveal } from '@/components/motion/reveal'
import { Marquee } from '@/components/motion/marquee'
import { CountUp } from '@/components/motion/count-up'
import { Stars, ReviewCard } from '@/components/widgets/review-card'
import { ServiceShowcase } from '@/components/widgets/service-showcase'
import { DiagnosisPanel } from '@/components/widgets/diagnosis-panel'
import { DoorRail } from '@/components/widgets/door-rail'
import { BeforeAfter } from '@/components/widgets/before-after'
import { PriceEstimator } from '@/components/widgets/price-estimator'
import { homeReviews, reviewStats } from '@/constants/trust'
import { homeServices } from '@/constants/services'
import { featuredProject, projects } from '@/constants/projects'
import { QUOTE_PATH } from '@/constants/site'

const marqueeItems = [
    "Broken springs",
    "Openers & smart garage",
    "Off-track & cables",
    "Panel replacement",
    "New garage doors",
    "Commercial doors",
    "Tune-up & maintenance"
]

export const ProofSection: FC = (): ReactNode => {
    return (
        <section id='reviews' className='relative py-24 md:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <Reveal className='lg:col-span-4'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-slate'>
                        In their own words
                    </p>

                    <div className='mt-6 flex items-end gap-4'>
                        <CountUp
                            value={4.9}
                            decimals={1}
                            className='font-heading text-[6.5rem] font-semibold leading-[0.8] tracking-[-0.06em] text-ink'
                        />

                        <div className='pb-2'>
                            <Stars />

                            <p className='mt-2 font-mono text-[12px] text-slate'>
                                across {reviewStats.count} verified reviews
                            </p>
                        </div>
                    </div>

                    <ul className='mt-10 space-y-4'>
                        {reviewStats.mentions.map(mention => (
                            <li key={mention.label}>
                                <div className='flex justify-between text-[14px]'>
                                    <span className='text-ink'>{mention.label}</span>
                                    <span className='font-mono text-slate'>{mention.count}</span>
                                </div>

                                <div className='mt-2 h-1 overflow-hidden rounded-full bg-sand'>
                                    <div className='h-full rounded-full bg-ember' style={{ width: `${(mention.count / 412) * 100}%` }} />
                                </div>
                            </li>
                        ))}
                    </ul>

                    <TransitionLink href='/about#reviews' className='mt-8 inline-flex items-center gap-1.5 text-[15px] font-medium text-ink underline-offset-4 hover:underline'>
                        Read more reviews
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </TransitionLink>
                </Reveal>

                <Reveal selector=':scope > *' className='grid gap-4 md:grid-cols-3 lg:col-span-8'>
                    {homeReviews.map((review, index) => (
                        <ReviewCard key={review.name} review={review} className={cn(index === 1 && 'md:translate-y-8')} />
                    ))}
                </Reveal>
            </Container>

            <div className='mt-24 border-y border-ink/10 py-6 md:mt-32'>
                <Marquee trackClassName='gap-10 pr-10'>
                    {marqueeItems.map(item => (
                        <span key={item} className='flex items-center gap-10 whitespace-nowrap font-heading text-[2rem] font-semibold tracking-[-0.03em] text-ink md:text-[2.75rem]'>
                            {item}
                            <span className='size-2.5 rotate-45 bg-ember' aria-hidden='true' />
                        </span>
                    ))}
                </Marquee>
            </div>
        </section>
    )
}

export const DiagnoseSection: FC = (): ReactNode => {
    return (
        <section id='diagnose' className='bg-sand/60 py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="30-second check"
                    title="Tell us what the door is doing."
                    description="Choose the symptom that sounds most like yours. We'll show the likely fix, how quickly it needs attention and the usual cost, then carry your answer into the quote form for you."
                />

                <div className='mt-14'>
                    <DiagnosisPanel />
                </div>
            </Container>
        </section>
    )
}

export const ServicesSection: FC = (): ReactNode => {
    return (
        <section id='services' className='py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="Services"
                    title="The work we know inside out."
                    description="Seven services, one standard: each visit opens with a 21-point safety inspection and a price in writing."
                    action={(
                        <ButtonLink href='/services' variant='outline'>
                            Explore every service
                        </ButtonLink>
                    )}
                />

                <ServiceShowcase services={homeServices} className='mt-14' />
            </Container>
        </section>
    )
}

export const DoorsSection: FC = (): ReactNode => {
    return (
        <section id='doors' className='overflow-hidden bg-paper py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="The door collection"
                    title="A new door changes the whole house."
                    description="Every model here is one we fit ourselves, shown with an installed price that already covers taking the old door away. Save up to 4 to compare side by side."
                    action={(
                        <div className='flex flex-wrap gap-3'>
                            <ButtonLink href='/new-doors' variant='dark'>
                                Browse and filter
                            </ButtonLink>

                            <ButtonLink href={`${QUOTE_PATH}?service=new-doors&entry=help_me_choose`} variant='outline'>
                                Help me choose
                            </ButtonLink>
                        </div>
                    )}
                />
            </Container>

            <div className='mt-14'>
                <DoorRail />
            </div>
        </section>
    )
}

export const RecentWorkSection: FC = (): ReactNode => {
    const others = projects.filter(project => project.slug !== featuredProject.slug).slice(0, 3)

    return (
        <section id='recent-work' className='relative overflow-hidden bg-ink py-24 text-cream md:py-32'>
            <Container>
                <SectionHeading
                    tone='light'
                    eyebrow="From the job sheet"
                    title="Slide across. See the difference."
                    description="Each project records the door that came down, the one that went up and the hours we spent on site."
                    action={(
                        <ButtonLink href='/projects' variant='outline-light'>
                            Browse every project
                        </ButtonLink>
                    )}
                />

                <Reveal className='mt-14' y={60}>
                    <figure>
                        <BeforeAfter
                            before={featuredProject.before}
                            after={featuredProject.after}
                            alt={featuredProject.alt}
                            className='aspect-[4/3] rounded-[28px] md:aspect-[16/8] md:rounded-[36px]'
                        />

                        <figcaption className='mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                            <p className='max-w-3xl text-[15.5px] leading-relaxed text-cream/70'>
                                {featuredProject.caption}
                            </p>

                            <TransitionLink
                                href={`/projects/${featuredProject.slug}`}
                                className='inline-flex shrink-0 items-center gap-2 text-[15px] font-medium text-cream underline-offset-4 hover:underline'
                            >
                                See this project
                                <ArrowUpRight className='size-4' aria-hidden='true' />
                            </TransitionLink>
                        </figcaption>
                    </figure>
                </Reveal>

                <Reveal selector=':scope > *' className='mt-14 grid grid-cols-1 gap-4 md:grid-cols-3'>
                    {others.map(project => (
                        <TransitionLink
                            key={project.slug}
                            href={`/projects/${project.slug}`}
                            className='group flex min-w-0 items-center gap-4 rounded-[22px] border border-cream/10 p-3 pr-5 transition-colors hover:border-cream/30 hover:bg-cream/[0.04]'
                        >
                            <span className='relative size-20 shrink-0 overflow-hidden rounded-2xl bg-graphite'>
                                <Image
                                    src={project.cover}
                                    alt={project.alt}
                                    fill
                                    sizes='80px'
                                    className='object-cover transition-transform duration-700 ease-expo group-hover:scale-110'
                                />
                            </span>

                            <span className='min-w-0 flex-1'>
                                <span className='block font-mono text-[10.5px] uppercase tracking-[0.16em] text-cream/45'>{project.category}</span>
                                <span className='mt-1 block line-clamp-2 font-heading text-[1.15rem] leading-snug md:truncate font-semibold tracking-[-0.02em]'>{project.title}</span>
                            </span>

                            <ArrowUpRight className='size-4 shrink-0 text-cream/40 transition-colors group-hover:text-ember-soft' aria-hidden='true' />
                        </TransitionLink>
                    ))}
                </Reveal>
            </Container>
        </section>
    )
}

export const PriceGuideSection: FC = (): ReactNode => {
    return (
        <section id='prices' className='py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="Pricing, in the open"
                    title="Know the cost before you pick up the phone."
                    description="Select whatever you think the door needs and watch the typical range for Riverton-area homes add up, parts and labor included."
                    action={(
                        <ButtonLink href='/pricing' variant='outline'>
                            Full price list and fees
                        </ButtonLink>
                    )}
                />

                <div className='mt-14'>
                    <PriceEstimator />
                </div>
            </Container>
        </section>
    )
}
